import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/store';

import { DocumentType } from 'app/types/document-type';
import { AsyncSelectOptions } from 'app/types/select-options';

import { Driver } from 'app/services/api/models/Driver';
import { Equipment } from 'app/services/api/models/Equipment';
import { DriverCalc } from 'app/services/api/models/DriverCalc';
import { DriverDaily } from 'app/services/api/models/DriverDaily';
import { DriverHistory } from 'app/services/api/models/DriverHistory';
import { UserPreference } from 'app/services/api/models/UserPreference';
import { AccountProperty } from 'app/services/api/models/AccountProperty';

import { driverLogsState } from './driverLogsState';
import {
  DecoratedDriver,
  DecoratedDriverHistory,
} from 'app/pages/hos/DriverLogs/types';

export const driverLogsSlice = createSlice({
  name: 'driverLogs',
  initialState: driverLogsState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    // ---------------------------------------------------------
    // MODAL TOGGLERS
    // ---------------------------------------------------------
    setToggleRecapModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleRecapModal = action.payload;
    },
    setToggleDriverLogFormModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleDriverLogFormModal = action.payload;
    },
    setToggleMultipleEditModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleMultipleEditModal = action.payload;
    },
    setToggleUploadDriverLogEditModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleUploadDriverLogEditModal = action.payload;
    },
    setToggleAddShipmentModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleAddShipmentModal = action.payload;
    },
    setToggleCreateUpdateRemarksModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleCreateUpdateRemarksModal = action.payload;
    },
    setToggleDeleteRemarksModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleDeleteRemarksModal = action.payload;
    },
    setToggleDrivingExceptionsModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleDrivingExceptionsModal = action.payload;
    },
    setToggleCreateUpdateStatusModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleCreateUpdateStatusModal = action.payload;
    },
    setToggleReassignDriveTimeModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleReassignDriveTimeModal = action.payload;
    },
    setToggleChangeCycleModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleChangeCycleModal = action.payload;
    },
    setToggleChangeStartTimeModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleChangeStartTimeModal = action.payload;
    },
    setToggleAdditionalHoursModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleAdditionalHoursModal = action.payload;
    },
    setToggleDrivingViolationDetailsModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleDrivingViolationDetailsModal = action.payload;
    },
    setToggleExportFileModal: (state, action: PayloadAction<boolean>) => {
      state.isToggleExportFileModal = action.payload;
    },
    setToggleDriverDailyDocumentsModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isToggleDriverDailyDocumentsModal = action.payload;
    },

    // ---------------------------------------------------------
    // SHOW ALL/EDITS CHECKBOXES
    // ---------------------------------------------------------
    setShowAll: (state, action: PayloadAction<boolean>) => {
      state.isShowAll = action.payload;
    },
    setShowEdits: (state, action: PayloadAction<boolean>) => {
      state.isShowEdits = action.payload;
    },

    // ---------------------------------------------------------
    // TOGGLES DRIVER'S TIMEZONE CHECKBOX
    // ---------------------------------------------------------
    setToggleDriversTimezone: (state, action: PayloadAction<boolean>) => {
      state.isToggleDriversTimezone = action.payload;
    },
    // ---------------------------------------------------------
    // TOGGLES GRID VIEW EDIT FORM
    // ---------------------------------------------------------
    setToggleGridViewEditForm: (state, action: PayloadAction<boolean>) => {
      state.isToggleGridViewEditForm = action.payload;
    },

    // ---------------------------------------------------------
    // API REQUEST STATUSES
    // ---------------------------------------------------------
    setInitializeFileExportRequestPending: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isInitializeFileExportRequestPending = action.payload;
    },
    setInitializeFetchDataRequestPending: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isInitializingFetchDataRequestPending = action.payload;
    },

    // ---------------------------------------------------------
    // DATA SOURCE
    // ---------------------------------------------------------
    setSelectedDate: (state, action: PayloadAction<string>) => {
      state.selectedDate = action.payload;
    },
    setSelectedDriver: (
      state,
      action: PayloadAction<AsyncSelectOptions | null>,
    ) => {
      state.selectedDriver = action.payload;
    },
    setDriver: (state, action: PayloadAction<Driver | null>) => {
      state.driver = action.payload;
    },
    setDecoratedDriver: (
      state,
      action: PayloadAction<DecoratedDriver | null>,
    ) => {
      state.decoratedDriver = action.payload;
    },
    setDriverCalcs: (state, action: PayloadAction<Array<DriverCalc>>) => {
      state.driverCalcs = action.payload;
    },
    setEquipments: (state, action: PayloadAction<Array<Equipment>>) => {
      state.equipments = action.payload;
    },
    setDriverDailies: (state, action: PayloadAction<Array<DriverDaily>>) => {
      state.driverDailies = action.payload;
    },
    setDriverHistories: (
      state,
      action: PayloadAction<Array<DriverHistory>>,
    ) => {
      state.driverHistories = action.payload;
    },
    setDecoratedDriverHistories: (
      state,
      action: PayloadAction<Array<DecoratedDriverHistory>>,
    ) => {
      state.decoratedDriverHistories = action.payload;
    },
    setDriverHistoryLogToEdit: (
      state,
      action: PayloadAction<DecoratedDriverHistory | null>,
    ) => {
      state.driverHistoryLogToEdit = action.payload;
    },
    setAccountProperties: (
      state,
      action: PayloadAction<Array<AccountProperty>>,
    ) => {
      state.accountProperties = action.payload;
    },
    setUserPreferences: (
      state,
      action: PayloadAction<Array<UserPreference>>,
    ) => {
      state.userPreferences = action.payload;
    },
    setDocumentType: (state, action: PayloadAction<DocumentType>) => {
      state.documentType = action.payload;
    },
    setRefetchDataCount: (state, action: PayloadAction<number>) => {
      state.refetchDataCount = action.payload;
    },
  },
});

/* ------- ACTION CREATORS ------- */
export const { actions: driverLogsActions } = driverLogsSlice;

/* ------- STATE SELECTORS ------- */
export const selectDriverLogs = (state: RootState) =>
  state.driverLogs || driverLogsState;

// Modal Togglers
export const makeSelectToggleRecapModal = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.isToggleRecapModal);

export const makeSelectToggleDriverLogFormModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleDriverLogFormModal,
  );

export const makeSelectToggleMultipleEditModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleMultipleEditModal,
  );

export const makeSelectToggleUploadDriverLogEditModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleUploadDriverLogEditModal,
  );

export const makeSelectToggleAddShipmentModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleAddShipmentModal,
  );

export const makeSelectToggleDeleteRemarksModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleDeleteRemarksModal,
  );

export const makeSelectToggleCreateUpdateRemarksModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleCreateUpdateRemarksModal,
  );

export const makeSelectToggleDrivingExceptionsModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleDrivingExceptionsModal,
  );

export const makeSelectToggleCreateUpdateStatusModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleCreateUpdateStatusModal,
  );

export const makeSelectToggleReassignDriveTimeModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleReassignDriveTimeModal,
  );

export const makeSelectToggleChangeCycleModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleChangeCycleModal,
  );

export const makeSelectToggleChangeStartTimeModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleChangeStartTimeModal,
  );

export const makeSelectToggleAdditionalHoursModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleAdditionalHoursModal,
  );

export const makeSelectToggleDrivingViolationDetailsModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleDrivingViolationDetailsModal,
  );

export const makeSelectToggleExportFileModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleExportFileModal,
  );

export const makeSelectToggleDriverDailyDocumentsModal = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleDriverDailyDocumentsModal,
  );

// Show All/Edits Checkboxes
export const makeSelectShowAll = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.isShowAll);

export const makeSelectShowEdits = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.isShowEdits);

// Toggles the driver's timezone
export const makeSelectToggleDriversTimezone = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleDriversTimezone,
  );
// Toggle the grid-view edit form
export const makeSelectToggleGridViewEditForm = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isToggleGridViewEditForm,
  );

// API Request Statuses
export const makeSelectInitializeFileExportRequestPending = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isInitializeFileExportRequestPending,
  );
export const makeSelectInitializeFetchDataRequestPending = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.isInitializingFetchDataRequestPending,
  );

// Data Sources
export const makeSelectLogDate = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.selectedDate);

export const makeSelectSelectedDriver = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.selectedDriver);

export const makeSelectEquipments = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.equipments);

export const makeSelectDriverCalcs = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.driverCalcs);

export const makeSelectDriver = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.driver);

export const makeSelectDecoratedDriver = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.decoratedDriver);

export const makeSelectDriverDailies = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.driverDailies);

export const makeSelectDriverHistories = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.driverHistories);

export const makeSelectDecoratedDriverHistories = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.decoratedDriverHistories,
  );

export const makeSelectDriverHistoryLogToEdit = () =>
  createSelector(
    selectDriverLogs,
    driverLogs => driverLogs.driverHistoryLogToEdit,
  );

export const makeSelectAccountProperties = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.accountProperties);

export const makeSelectUserPreferences = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.userPreferences);

export const makeSelectRefetchDataCount = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.refetchDataCount);

export const makeSelectDocumentType = () =>
  createSelector(selectDriverLogs, driverLogs => driverLogs.documentType);

export default driverLogsSlice.reducer;
